import type {
  SchoolBasedSelectionActivationResult,
  SchoolBasedSelectionCandidate,
  SchoolBasedSelectionCourseForm,
  SchoolBasedSelectionCourseStudent,
  SchoolBasedSelectionMember,
  SchoolBasedSelectionScope,
  SchoolBasedSelectionSlot,
  SchoolBasedSelectionTask,
  SchoolBasedSelectionTaskForm
} from '@/types/school-based-selection'
import { http } from './request'

/** datetime-local 值 → 后端 "yyyy-MM-dd HH:mm:ss"。 */
function toServerDateTime(value: string): string | null {
  if (!value) return null
  const normalized = value.replace('T', ' ')
  return normalized.length === 16 ? `${normalized}:00` : normalized
}

/** 后端 "yyyy-MM-dd HH:mm:ss" → datetime-local 值。 */
export function toLocalDateTime(value: string | null): string {
  if (!value) return ''
  return value.replace(' ', 'T').slice(0, 16)
}

function toCreatePayload(form: SchoolBasedSelectionTaskForm): Record<string, unknown> {
  return {
    name: form.name.trim(),
    semesterId: form.semesterId,
    bellScheduleId: form.bellScheduleId,
    // 工作日数由后端从作息方案推导，不再传入
    startTime: toServerDateTime(form.startTime),
    endTime: toServerDateTime(form.endTime),
    maxCoursesPerStudent: form.maxCoursesPerStudent,
    allowChange: form.allowChange,
    memberIds: form.memberIds,
    gradeId: form.gradeId,
    participantClassIds: form.participantClassIds
  }
}

function toCoursePayload(form: SchoolBasedSelectionCourseForm): Record<string, unknown> {
  return {
    sourceCourseId: form.sourceCourseId,
    name: form.name.trim() || null,
    description: form.description.trim() || null,
    teacherIds: form.teacherIds,
    capacity: form.capacity,
    weeklyFrequency: form.weeklyFrequency
  }
}

export const schoolBasedSelectionApi = {
  // ── 任务生命周期 ──

  async listTasks(): Promise<SchoolBasedSelectionTask[]> {
    const response = await http.get<SchoolBasedSelectionTask[]>('/school-based-selection/tasks')
    return response.data ?? []
  },

  async getTask(id: number): Promise<SchoolBasedSelectionTask> {
    const response = await http.get<SchoolBasedSelectionTask>(`/school-based-selection/tasks/${id}`)
    return response.data
  },

  async createTask(form: SchoolBasedSelectionTaskForm): Promise<SchoolBasedSelectionTask> {
    const response = await http.post<SchoolBasedSelectionTask>('/school-based-selection/tasks', toCreatePayload(form))
    return response.data
  },

  async updateTask(id: number, payload: Partial<Record<string, unknown>>): Promise<SchoolBasedSelectionTask> {
    const response = await http.put<SchoolBasedSelectionTask>(`/school-based-selection/tasks/${id}`, payload)
    return response.data
  },

  async removeTask(id: number): Promise<void> {
    await http.delete<void>(`/school-based-selection/tasks/${id}`)
  },

  async publishTask(id: number): Promise<SchoolBasedSelectionTask> {
    const response = await http.post<SchoolBasedSelectionTask>(`/school-based-selection/tasks/${id}/publish`)
    return response.data
  },

  async withdrawTask(id: number): Promise<SchoolBasedSelectionTask> {
    const response = await http.post<SchoolBasedSelectionTask>(`/school-based-selection/tasks/${id}/withdraw`)
    return response.data
  },

  async copyTask(id: number): Promise<SchoolBasedSelectionTask> {
    const response = await http.post<SchoolBasedSelectionTask>(`/school-based-selection/tasks/${id}/copy`)
    return response.data
  },

  async closeTask(id: number): Promise<SchoolBasedSelectionTask> {
    const response = await http.post<SchoolBasedSelectionTask>(`/school-based-selection/tasks/${id}/close`)
    return response.data
  },

  /** 启用：已截止任务物化名单 + 课位为课时链。 */
  async activateTask(id: number): Promise<SchoolBasedSelectionActivationResult> {
    const response = await http.post<SchoolBasedSelectionActivationResult>(`/school-based-selection/tasks/${id}/activate`)
    return response.data
  },

  /** 停用：作废课时链，任务回到已截止未启用态。 */
  async deactivateTask(id: number): Promise<SchoolBasedSelectionTask> {
    const response = await http.post<SchoolBasedSelectionTask>(`/school-based-selection/tasks/${id}/deactivate`)
    return response.data
  },

  // ── 课程维护（草稿态） ──

  async addCourse(taskId: number, form: SchoolBasedSelectionCourseForm): Promise<void> {
    await http.post<void>(`/school-based-selection/tasks/${taskId}/courses`, toCoursePayload(form))
  },

  async updateCourse(taskId: number, courseId: number, form: SchoolBasedSelectionCourseForm): Promise<void> {
    await http.put<void>(`/school-based-selection/tasks/${taskId}/courses/${courseId}`, toCoursePayload(form))
  },

  async removeCourse(taskId: number, courseId: number): Promise<void> {
    await http.delete<void>(`/school-based-selection/tasks/${taskId}/courses/${courseId}`)
  },

  /** 课位保存（整课替换：拖拽结果）。 */
  async saveCourseSlots(taskId: number, courseId: number, slots: SchoolBasedSelectionSlot[]): Promise<void> {
    await http.post<void>(`/school-based-selection/tasks/${taskId}/courses/${courseId}/slots`, { slots })
  },

  async listCourseStudents(taskId: number, courseId: number): Promise<SchoolBasedSelectionCourseStudent[]> {
    const response = await http.get<SchoolBasedSelectionCourseStudent[]>(
      `/school-based-selection/tasks/${taskId}/courses/${courseId}/students`)
    return response.data ?? []
  },

  // ── 圈人 ──

  async getCreationScope(gradeId: number): Promise<SchoolBasedSelectionScope> {
    const response = await http.get<SchoolBasedSelectionScope>(`/school-based-selection/scope?gradeId=${gradeId}`)
    return response.data
  },

  async listScopeStudents(gradeId: number, classIds: number[]): Promise<SchoolBasedSelectionCandidate[]> {
    const params = new URLSearchParams()
    params.set('gradeId', String(gradeId))
    classIds.forEach((id) => params.append('classIds', String(id)))
    const response = await http.get<SchoolBasedSelectionCandidate[]>(`/school-based-selection/scope/students?${params.toString()}`)
    return response.data ?? []
  },

  // ── 名单 ──

  async listMembers(taskId: number): Promise<SchoolBasedSelectionMember[]> {
    const response = await http.get<SchoolBasedSelectionMember[]>(`/school-based-selection/tasks/${taskId}/members`)
    return response.data ?? []
  },

  async addMembers(taskId: number, studentIds: number[]): Promise<number> {
    const response = await http.post<number>(`/school-based-selection/tasks/${taskId}/members`, { studentIds })
    return response.data ?? 0
  },

  async removeMember(taskId: number, studentId: number): Promise<void> {
    await http.delete<void>(`/school-based-selection/tasks/${taskId}/members/${studentId}`)
  },

  // ── 代选 / 代退 / 指派 ──

  async teacherEnroll(taskId: number, studentId: number, taskCourseId: number): Promise<void> {
    await http.post<void>(`/school-based-selection/tasks/${taskId}/enrollments`, { studentId, taskCourseId })
  },

  async teacherDrop(taskId: number, studentId: number, taskCourseId: number): Promise<void> {
    await http.post<void>(`/school-based-selection/tasks/${taskId}/enrollments/drop`, { studentId, taskCourseId })
  },

  async assign(taskId: number, studentId: number, taskCourseId: number): Promise<void> {
    await http.post<void>(`/school-based-selection/tasks/${taskId}/assign`, { studentId, taskCourseId })
  },

  // ── 导出 ──

  async exportTaskExcel(taskId: number, sheets: string[]): Promise<Blob> {
    return http.getBlob(`/school-based-selection/tasks/${taskId}/export?sheets=${sheets.join(',')}`)
  }
}
